// A list the reader drags into order, by its rows' handles (decision 0024, the batting order). Pointer events, so a finger works
// as a mouse does: the handle takes the pointer, the dragged row follows it, the rows it passes slide out of its way, and on release
// the page is told where the row went. Nothing in the DOM is moved: Blazor redraws the list in its new order.
export interface Sorter { stop(): void }

interface Target { invokeMethodAsync(name: string, ...args: unknown[]): Promise<unknown> }

const ROW = 'tr[data-testid=player]';

/** Where a row dragged from `from` by `dy` pixels now sits, among rows whose places are `rects`. */
export function placeOf(rects: { top: number; height: number }[], from: number, dy: number): number {
  const y = rects[from].top + rects[from].height / 2 + dy;
  // Its place is the number of other rows whose middle it has passed.
  return rects.filter((r, i) => i !== from && r.top + r.height / 2 < y).length;
}

/** How far row `i` slides to make room while the row at `from` is held over `to`: one row's height up, down or not at all. */
export function shiftOf(i: number, from: number, to: number, height: number): number {
  return i > from && i <= to ? -height : i < from && i >= to ? height : 0;
}

export function sortable(list: HTMLElement, target: Target): Sorter {
  const start = (down: PointerEvent): void => {
    const handle = (down.target as Element | null)?.closest?.<HTMLElement>('[data-drag]');
    const row = handle?.closest<HTMLElement>(ROW);
    if (!handle || !row || !list.contains(row)) return;
    down.preventDefault();
    const rows = Array.from(list.querySelectorAll<HTMLElement>(ROW));
    const from = rows.indexOf(row);
    const tops = rows.map(r => r.getBoundingClientRect());
    const height = tops[from].height;
    let to = from;
    list.classList.add('sorting');
    row.classList.add('dragging');
    handle.setPointerCapture?.(down.pointerId);
    const move = (e: PointerEvent): void => {
      const dy = e.clientY - down.clientY;
      row.style.transform = `translateY(${dy}px)`;
      to = placeOf(tops, from, dy);
      rows.forEach((r, i) => {
        if (i === from) return;
        const shift = shiftOf(i, from, to, height);
        r.style.transform = shift ? `translateY(${shift}px)` : '';
      });
    };
    const end = (): void => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', end);
      handle.removeEventListener('pointercancel', end);
      rows.forEach(r => { r.style.transform = ''; });
      row.classList.remove('dragging');
      list.classList.remove('sorting');
      if (to !== from) void target.invokeMethodAsync('Moved', from, to);
    };
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
  };
  list.addEventListener('pointerdown', start);
  return { stop: () => list.removeEventListener('pointerdown', start) };
}
